import { LoadingSpinner } from "@connected-repo/ui-mui/components/LoadingSpinner";
import { lazy, Suspense } from "react";
import { createBrowserRouter, type RouteObject, redirect } from "react-router";
import { CustomErrorBoundary } from "@frontend/components/error_fallback";

type NavbarFields = {
	nb_icon?: string;
};

type BaseRouterWithNavbar = RouteObject & NavbarFields;
type ReactRouterWithNavbar = BaseRouterWithNavbar & {
	children?: ReactRouterWithNavbar[];
};

// HydrateFallback component for initial app loading
const HydrateFallback = () => {
	return (
		<LoadingSpinner
			fullScreen
			text="ExpoWiz"
			subtext="Syncing your environment..."
			color="inherit"
		/>
	);
};

const AppLayout = lazy(() => import("@frontend/components/layout/AppLayout").then(m => ({ default: m.AppLayout })));

const routerObjectWithNavbar: ReactRouterWithNavbar[] = [
	{
		path: "/",
		errorElement: <CustomErrorBoundary />,
		hydrateFallbackElement: <HydrateFallback />,
		children: [
			{
				index: true,
				loader: async () => {
					// Direct redirect based on cached session to avoid triple-redirect waterfall
					const { getAuthCache } = await import("@frontend/utils/auth.persistence");
					const cached = getAuthCache();
					if (cached) {
						return redirect("/leads/create");
					}
					return redirect("/auth/login");
				},
			},
			{
				path: "auth/*",
				Component: lazy(() => import("@frontend/modules/auth/auth.router")),
			},
			// Authenticated routes with AppLayout
			{
				element: (
					<Suspense fallback={<HydrateFallback />}>
						<AppLayout />
					</Suspense>
				),
				loader: (args) => import("@frontend/utils/auth.loader").then(m => m.authLoader(args)),
				children: [
					{
						path: "dashboard",
						Component: lazy(() => import("@frontend/pages/Dashboard.page")),
					},
					{
						path: "leads/*",
						Component: lazy(() => import("@frontend/modules/leads/leads.router")),
					},
					{
						path: "sync-status",
						Component: lazy(() => import("@frontend/pages/SyncStatus.page")),
					},

					{
						path: "teams/*",
						Component: lazy(() => import("@frontend/modules/teams/teams.router")),
					},
					{
						path: "profile",
						Component: lazy(() => import("@frontend/pages/Dashboard.page")), // TODO: Create Profile page
					},
				],
			},
		],
	},
];

export const router = createBrowserRouter(routerObjectWithNavbar);
